import { ContextualAttachment } from '@/features/ai-agents/shared/models';
import { truncate } from '@/utils/types';
import { useLoggedInAccount } from '@/features/account-context';

const MAX_REPEATED_LINE_ITEMS = 10;
const TRUNCATION_THRESHOLD = 120_000;

function getStructuralSignature(element: Element): string {
  const tag = element.tagName;
  const classes = Array.from(element.classList).sort().join(`,`);
  const childSignatures = Array.from(element.children)
    .map(getStructuralSignature)
    .join(`|`);
  return `${tag}[${classes}]{${childSignatures}}`;
}

function truncateRepeatedSiblings(parent: Element): void {
  const childrenBySignature = new Map<string, Element[]>();

  Array.from(parent.children).forEach((child) => {
    const sig = getStructuralSignature(child);
    if (!childrenBySignature.has(sig)) childrenBySignature.set(sig, []);
    childrenBySignature.get(sig)!.push(child);
  });

  childrenBySignature.forEach((siblings) => {
    if (siblings.length > MAX_REPEATED_LINE_ITEMS) {
      siblings.slice(MAX_REPEATED_LINE_ITEMS).forEach((el) => el.remove());
    }
  });

  Array.from(parent.children).forEach((child) =>
    truncateRepeatedSiblings(child),
  );
}

function normalizeWhitespace(html: string): string {
  return html.replace(/[ \t]{2,}/g, ` `).replace(/(\s*\n\s*){2,}/g, `\n`);
}

function countNonSpaceChars(html: string): number {
  return html.replace(/\s/g, ``).length;
}

function truncateRepeatedLineItems(html: string): string {
  const ctx = useLoggedInAccount();
  const isGemini =
    ctx.store?.defaultAiModels[`section-modifier`]?.includes(`gemini`) || true;

  if (!isGemini && countNonSpaceChars(html) < TRUNCATION_THRESHOLD) return html;
  console.log(
    `vsly-ai`,
    `Truncating HTML since threshold > ${TRUNCATION_THRESHOLD} chars`,
  );
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, `text/html`);
  truncateRepeatedSiblings(doc.body);
  return normalizeWhitespace(doc.body.innerHTML);
}

export function extractHTMLAttachment(
  selector: string,
  runInIframe: (callback: (doc: Document, win: Window) => void) => void,
  editable = true,
): ContextualAttachment {
  let payload;
  runInIframe((doc) => {
    const element = doc.querySelector(`${selector}`);
    const innerHTML = element?.innerHTML || ``;
    const truncatedHTML = truncateRepeatedLineItems(innerHTML);
    payload = `<!-- Base selector: ${selector} -->\n${truncatedHTML}`;
  });

  return {
    id: selector,
    caption: truncate(selector, 25),
    payload,
    type: `application/html`,
    editable,
    selector,
  };
}

export function extractCSSAttachment(
  selector: string,
  runInIframe: (callback: (doc: Document, win: Window) => void) => void,
  editable = false,
): ContextualAttachment {
  const cssContent = extractCSSContent(selector, runInIframe);

  return {
    id: `${selector}-css`,
    caption: truncate(selector, 25),
    payload: cssContent,
    type: `text/css`,
    editable,
    selector,
  };
}

function extractCSSContent(
  selector: string,
  runInIframe: (callback: (doc: Document, win: Window) => void) => void,
): string {
  let cssContent = `/* Base selector: ${selector} */\n`;

  runInIframe((doc) => {
    const rootElement = doc.querySelector(selector);
    if (!rootElement) return;

    // 1. Collect all unique class names used within the element subtree
    const usedClasses = new Set<string>();
    const elements = [
      rootElement,
      ...Array.from(rootElement.querySelectorAll(`*`)),
    ];

    elements.forEach((el) => {
      el.classList.forEach((cls) => usedClasses.add(cls));
    });

    // 2. Iterate over all stylesheets to find matching rules
    const styleSheets = Array.from(doc.styleSheets);
    styleSheets.forEach((sheet) => {
      try {
        // Accessing cssRules might fail for cross-origin stylesheets
        const rules = Array.from(sheet.cssRules || []);
        rules.forEach((rule) => {
          // Check if the selector text contains any of our used classes
          // This is a naive check; for strict correctness, one would parse the selector
          const { selectorText } = rule as CSSStyleRule;
          if (!selectorText) return;

          const involvesUsedClass = Array.from(usedClasses).some((cls) => {
            // Matches .classname at start, end, or surrounded by non-word chars
            const regex = new RegExp(`(^|[^\\w-])${cls}([^\\w-]|$)`);
            return regex.test(selectorText);
          });

          if (involvesUsedClass) {
            cssContent += `${rule.cssText}\n`;
          }
        });
      } catch (e) {
        console.warn(`Could not access stylesheet: ${sheet.href}`, e);
      }
    });
  });

  return cssContent;
}
